import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalyticsCommonTypes, PhoneAnalyticsTypes } from '@frontend/api-analytics';
import {
  AnalyticsHooks,
  AnalyticsStyles,
  AnalyticsUtils,
  DatesRangeFilterSelector,
  FiltersTray,
  HoursFilterSelector,
  LocationsFilterSelector,
} from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { TableFilters } from '@frontend/design-system';
import { defaultTimePeriod } from '../../constants';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { trackingIds } from '../../tracking-ids';
import { AdditionalPhoneFilters } from './additional-phone-filters';

const usePhoneFiltersManagement = () => {
  const { accessibleLocationData, selectedLocationIds: selectedLocationIdsInGLS } = useAppScopeStore();

  const { filters, hasCustomFilters, isDemoAccount, previousGlsLocationIds, initialize, setStore, resetToDefaults } =
    usePhoneAnalyticsShallowStore(
      'filters',
      'hasCustomFilters',
      'isDemoAccount',
      'previousGlsLocationIds',
      'initialize',
      'setStore',
      'resetToDefaults'
    );

  const [trayFilters, setTrayFilters] = useState<PhoneAnalyticsTypes.Filters>(filters);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const { hasAccessToMultipleLocations, isLoadingLocations, locationNames, preselectedAccessibleLocationIds } =
    AnalyticsHooks.useAnalyticsOrgLocations({
      isAllAccessScopeEnabled: true,
      isDemoAccount,
      module: 'PHONE',
    });

  const { TimePeriodDropDownSelector, defaultStartDate, defaultEndDate, selectedPeriod, timePeriods } =
    AnalyticsHooks.useTimePeriodDropDownSelector({
      defaultPeriod: defaultTimePeriod,
      values: {
        startDate: trayFilters.start_date,
        endDate: trayFilters.end_date,
      },
    });

  useEffect(() => {
    setTrayFilters(filters);
  }, [filters]);

  const hasErrors = useMemo(() => Object.values(errors).some(Boolean), [errors]);

  const currentGLSLocationIds = useMemo(
    () => [...selectedLocationIdsInGLS].sort().join(','),
    [selectedLocationIdsInGLS]
  );

  const handleUpdateTrayFilters = useCallback((newFilters: Partial<PhoneAnalyticsTypes.Filters>) => {
    setTrayFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const handleUpdateErrors = useCallback((error: AnalyticsCommonTypes.FilterSelectorError) => {
    setErrors((prev) => ({ ...prev, [error.name]: error.value }));
  }, []);

  const handleApplyTrayFilters = useCallback(() => {
    const hintText = selectedPeriod
      ? timePeriods[selectedPeriod].label
      : `${formatters.date(trayFilters.start_date)} - ${formatters.date(trayFilters.end_date)}`;

    setStore({
      filterHintText: hintText,
      filters: trayFilters,
    });
  }, [setStore, trayFilters, selectedPeriod, timePeriods]);

  const handleRevertLocalChanges = useCallback(() => {
    setTrayFilters(filters);
  }, [filters]);

  const handleResetFilters = useCallback(() => {
    resetToDefaults();
    setStore({ filterHintText: timePeriods[defaultTimePeriod]?.label || '' });
  }, [resetToDefaults, timePeriods, setStore]);

  // Reset filters when the Global Location Selector context changes
  useEffect(() => {
    if (previousGlsLocationIds && currentGLSLocationIds !== previousGlsLocationIds) {
      setStore({ previousGlsLocationIds: currentGLSLocationIds });
      resetToDefaults();
    } else if (!previousGlsLocationIds) {
      setStore({ previousGlsLocationIds: currentGLSLocationIds });
    }
  }, [currentGLSLocationIds, previousGlsLocationIds, setStore, resetToDefaults]);

  useEffect(() => {
    const baseLocationData = accessibleLocationData[preselectedAccessibleLocationIds[0]];
    initialize({
      accessibleLocationsNames: locationNames,
      filterHintText: timePeriods[defaultTimePeriod]?.label || '',
      filters: {
        start_date: defaultStartDate,
        end_date: defaultEndDate,
        location_id: preselectedAccessibleLocationIds,
        order_by_field: 'StartTime',
        order_by_asc: false,
        time_zone: baseLocationData?.timezone,
      },
    });
  }, [
    accessibleLocationData,
    defaultEndDate,
    defaultStartDate,
    initialize,
    locationNames,
    preselectedAccessibleLocationIds,
    timePeriods,
  ]);

  return {
    TimePeriodDropDownSelector,
    handlers: {
      handleUpdateTrayFilters,
      handleUpdateErrors,
      handleApplyTrayFilters,
      handleResetFilters,
      handleRevertLocalChanges,
    },
    hasAccessToMultipleLocations,
    hasCustomFilters,
    hasErrors,
    isLoadingLocations,
    locationNames,
    trayFilters,
  };
};

type PhoneAnalyticsFiltersProps = {
  isTableView?: boolean;
};

export const PhoneAnalyticsFiltersTray = ({ isTableView }: PhoneAnalyticsFiltersProps) => {
  const { t } = useTranslation('analytics-phone');

  const {
    TimePeriodDropDownSelector,
    handlers,
    hasAccessToMultipleLocations,
    hasCustomFilters,
    hasErrors,
    isLoadingLocations,
    locationNames,
    trayFilters,
  } = usePhoneFiltersManagement();

  return (
    <FiltersTray
      disableApply={hasErrors}
      headerLabel={t('Filter Phone Analytics')}
      onApplyFilters={handlers.handleApplyTrayFilters}
      onResetFilters={handlers.handleResetFilters}
      onRevertLocalChanges={handlers.handleRevertLocalChanges}
      showFilteredBadge={hasCustomFilters}
      trackingId={trackingIds.filtersOpen}
    >
      {hasAccessToMultipleLocations && (
        <TableFilters.Section sectionHeaderLabel={t('Location')}>
          <div css={AnalyticsStyles.filtersStyles.traySection}>
            <LocationsFilterSelector
              disabled={isLoadingLocations}
              locations={locationNames}
              onChange={(value) => handlers.handleUpdateTrayFilters({ location_id: value })}
              onError={handlers.handleUpdateErrors}
              value={trayFilters.location_id}
            />
          </div>
        </TableFilters.Section>
      )}

      <TableFilters.Section sectionHeaderLabel={t('Time')}>
        <div css={AnalyticsStyles.filtersStyles.traySection}>
          <TimePeriodDropDownSelector
            onChange={({ startDate, endDate }) =>
              handlers.handleUpdateTrayFilters({ start_date: startDate, end_date: endDate })
            }
            trackingIdBase={trackingIds.timePeriodOnTrayFilter}
          />
          <DatesRangeFilterSelector
            endDate={trayFilters.end_date}
            onChange={({ endDate, startDate }) =>
              handlers.handleUpdateTrayFilters({
                end_date: AnalyticsUtils.appendLocalTimeStamp(endDate),
                start_date: AnalyticsUtils.appendLocalTimeStamp(startDate),
              })
            }
            startDate={trayFilters.start_date}
          />
        </div>
      </TableFilters.Section>

      {isTableView ? (
        <AdditionalPhoneFilters filters={trayFilters} onChange={handlers.handleUpdateTrayFilters} />
      ) : (
        <TableFilters.Section sectionHeaderLabel={t('View')}>
          <HoursFilterSelector
            onChange={(value) => handlers.handleUpdateTrayFilters({ open_office: value === 'open-hours' })}
            value={trayFilters.open_office ? 'open-hours' : 'all-hours'}
          />
        </TableFilters.Section>
      )}
    </FiltersTray>
  );
};
